import { OauthLoginProvider } from 'multirpc-sdk';
import { useMemo } from 'react';

import { IUseQueryProps } from 'store/queries/types';
import { getQueryParams } from 'store/utils/getQueryParams';
import { useAppSelector } from 'store/useAppSelector';

import {
  selectAssociatedAccounts,
  selectAssociatedAccountsLoading,
  selectAssociatedAccountsState,
  useFetchAssociatedAccountsQuery,
  useLazyFetchAssociatedAccountsQuery,
} from '../actions/fetchAssociatedAccounts';

export interface IUseAssociatedAccounts extends IUseQueryProps {}

export const useAssociatedAccounts = ({
  skipFetching,
}: IUseAssociatedAccounts = {}) => {
  useFetchAssociatedAccountsQuery(
    getQueryParams({ params: undefined, skipFetching }),
  );

  const [handleFetchAssociatedAccounts] = useLazyFetchAssociatedAccountsQuery();

  const associatedAccounts = useAppSelector(selectAssociatedAccounts);

  const loading = useAppSelector(selectAssociatedAccountsLoading);

  const state = useAppSelector(selectAssociatedAccountsState);

  const {
    hasAuth0ProviderConnected,
    hasGithubProviderConnected,
    hasGoogleProviderConnected,
    isOnlyGoogleProviderConnected,
  } = useMemo(
    () => ({
      hasGoogleProviderConnected:
        associatedAccounts.length > 0 &&
        !!associatedAccounts.find(
          account => account.provider === OauthLoginProvider.Google,
        ),
      isOnlyGoogleProviderConnected:
        associatedAccounts.length === 1 &&
        associatedAccounts[0].provider === OauthLoginProvider.Google,
      hasGithubProviderConnected:
        associatedAccounts.length > 0 &&
        !!associatedAccounts.find(
          account => account.provider === OauthLoginProvider.Github,
        ),
      hasAuth0ProviderConnected:
        associatedAccounts.length > 0 &&
        !!associatedAccounts.find(
          account => account.provider === OauthLoginProvider.Auth0,
        ),
    }),
    [associatedAccounts],
  );

  return {
    associatedAccounts,
    handleFetchAssociatedAccounts,
    loading,
    state,
    hasGoogleProviderConnected,
    isOnlyGoogleProviderConnected,
    hasGithubProviderConnected,
    hasAuth0ProviderConnected,
  };
};
